'use client';

import { BookOpen } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { SetupConnectCopyField } from '@/components/setup/experience/SetupConnectCopyField';
import { SetupConnectTutorialContent } from '@/components/setup/experience/SetupConnectTutorialContent';
import type { SetupConnectTutorialStep } from '@/components/setup/experience/lib/connectTutorialTypes';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  SHOPIFY_APP_URL,
  SHOPIFY_REDIRECT_URL,
  SHOPIFY_REQUIRED_SCOPES,
} from '@/lib/utils/shopify-oauth-utils';

const TUTORIAL_ASSETS_BASE = import.meta.env.VITE_TUTORIAL_ASSETS_BASE_PATH ?? '/assets/shopify';

const OAUTH_TUTORIAL_IMAGE_FILES = [
  'step1-apps.png',
  'step2-develop-apps.png',
  'step3-dev-dashboard.png',
  'step4-create-app.png',
  'step5-scopes-redirect.png',
  'step6-release.png',
  'step7-credentials.png',
] as const;

const SCOPES_AND_REDIRECT_STEP_INDEX = 4;

/** Always-visible Shopify app setup guide shown beside the OAuth connect form. */
export function SetupShopifyOAuthGuidePanel({ className }: { className?: string }) {
  const { t } = useTranslation(['setup', 'integrations']);
  const [zoomedStep, setZoomedStep] = React.useState<SetupConnectTutorialStep | null>(null);

  // Preload every screenshot once so step navigation never flashes an empty frame
  React.useEffect(() => {
    for (const file of OAUTH_TUTORIAL_IMAGE_FILES) {
      const img = new Image();
      img.src = `${TUTORIAL_ASSETS_BASE}/tutov2/${file}`;
    }
  }, []);

  const steps = React.useMemo<SetupConnectTutorialStep[]>(
    () =>
      OAUTH_TUTORIAL_IMAGE_FILES.map((file, index) => {
        const stepNumber = index + 1;
        const title = t(`integrations:shopifyTutorial.oauth.step${stepNumber}Title`);

        return {
          id: `shopify-oauth-step-${stepNumber}`,
          title,
          instructions: t(`integrations:shopifyTutorial.oauth.step${stepNumber}Instructions`),
          image: `${TUTORIAL_ASSETS_BASE}/tutov2/${file}`,
          imageAlt: t('integrations:shopifyTutorial.stepImageAlt', { step: stepNumber, title }),
          content:
            index === SCOPES_AND_REDIRECT_STEP_INDEX ? (
              <div className="flex flex-col gap-2.5">
                <SetupConnectCopyField
                  label={t('integrations:shopifyTutorial.scopesLabel')}
                  value={SHOPIFY_REQUIRED_SCOPES}
                  copyAriaLabel={t('integrations:shopifyTutorial.copyLabel', {
                    label: t('integrations:shopifyTutorial.scopesLabel'),
                  })}
                />
                <SetupConnectCopyField
                  label={t('integrations:shopifyTutorial.appUrlLabel')}
                  value={SHOPIFY_APP_URL}
                  copyAriaLabel={t('integrations:shopifyTutorial.copyLabel', {
                    label: t('integrations:shopifyTutorial.appUrlLabel'),
                  })}
                />
                <SetupConnectCopyField
                  label={t('integrations:shopifyTutorial.redirectUrlLabel')}
                  value={SHOPIFY_REDIRECT_URL}
                  copyAriaLabel={t('integrations:shopifyTutorial.copyLabel', {
                    label: t('integrations:shopifyTutorial.redirectUrlLabel'),
                  })}
                />
              </div>
            ) : undefined,
        };
      }),
    [t],
  );

  return (
    <aside
      className={className}
      aria-label={t('setup:experience.connect.shopifyOAuthGuide.segmentsAriaLabel')}
    >
      <div className="border-border rounded-xl border bg-[color-mix(in_srgb,var(--muted)_12%,var(--card))]">
        <p className="text-muted-foreground m-0 flex items-center gap-1.5 px-5 pb-3 pt-4 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] sm:px-6">
          <BookOpen className="size-3.5" aria-hidden />
          {t('setup:experience.connect.shopifyOAuthGuide.kicker')}
        </p>

        <SetupConnectTutorialContent
          className="pb-4"
          layout="stacked"
          steps={steps}
          stepOfLabel={(current, total) =>
            t('setup:experience.connect.shopifyTutorialStepOf', { current, total })
          }
          segmentsAriaLabel={t('setup:experience.connect.shopifyOAuthGuide.segmentsAriaLabel')}
          goToStepAriaLabel={(step) => t('integrations:shopifyTutorial.goToStep', { step })}
          onImageClick={setZoomedStep}
          imageZoomAriaLabel={t('setup:experience.connect.shopifyOAuthGuide.zoomImage')}
        />
      </div>

      <Dialog open={Boolean(zoomedStep)} onOpenChange={(open) => !open && setZoomedStep(null)}>
        <DialogContent className="gap-3 sm:max-w-4xl">
          <DialogTitle className="text-base font-semibold leading-snug tracking-tight">
            {zoomedStep?.title}
          </DialogTitle>
          {zoomedStep?.image ? (
            <div className="overflow-hidden rounded-[0.625rem] border border-[color-mix(in_oklch,var(--border)_82%,transparent)] bg-[color-mix(in_oklch,var(--background)_88%,var(--card))]">
              <img
                src={zoomedStep.image}
                alt={zoomedStep.imageAlt ?? zoomedStep.title}
                className="block max-h-[72vh] w-full object-contain"
                draggable={false}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </aside>
  );
}
